import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type IAlert } from 'shared/ui/Alert/types'
import { AgentActions } from 'entities/AgentActions'
import { AgentErrors } from 'entities/Chatbot/model/AgentError'
import { type IAgentThirdStepPayload, type IChatbotFullData } from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { AgentBookAppointmentStepsStore } from 'pages/chatbot/pages/agentBookAppointment/store/agentBookAppointmentStepsStore'
import { ChatbotAction } from 'pages/chatbot/store/chatbotActionStore'
import { type IHandleError } from 'pages/chatbot/pages/agentBookAppointment/store/type'

type SectionUnion = 'dataCollection' | 'goalMet' | 'goalNotMet' | 'qualification' | 'handoff'

export class AgentActionsStore {
  private _openedSections: Set<SectionUnion> = new Set(['dataCollection'])
  private _successActionsModel = new AgentActions(null, { sendMessageAutoMode: true })
  private _fallbackActionsModel = new AgentActions(null, { sendMessageAutoMode: true })
  private _successActionsErrors = new AgentErrors(this)
  private _fallbackActionsErrors = new AgentErrors(this)
  private _qualifiedActionsErrors = new AgentErrors(this)
  private _disqualifiedActionsErrors = new AgentErrors(this)
  private _disposeUpdateOpenedSections: IReactionDisposer | null = null

  private _successActions: ChatbotAction
  private _fallbackActions: ChatbotAction
  private _loading = false

  constructor(
    private _stepsStore: AgentBookAppointmentStepsStore,
    private _entities: IEntitiesStore
  ) {
    this._successActions = new ChatbotAction(
      this._successActionsModel,
      this._successActionsErrors.successActions,
      this._entities
    )
    this._fallbackActions = new ChatbotAction(
      this._fallbackActionsModel,
      this._fallbackActionsErrors.fallbackActions,
      this._entities
    )

    makeAutoObservable(this)

    this.reactionUpdateOpenedSections()
  }

  reactionUpdateOpenedSections = () => {
    this._disposeUpdateOpenedSections?.()
    this._disposeUpdateOpenedSections = reaction(
      () => this.isChatBookingType,
      () => {
        this.handleBookingTypeChange()
      }
    )
  }

  init = (data: IChatbotFullData) => {
    this._successActionsModel.syncOrigin(data.success_actions)
    this._fallbackActionsModel.syncOrigin(data.fallback_actions)

    if (this.bookingType) {
      this.handleBookingTypeChange()
    }
  }

  get chatbot() {
    return this._stepsStore.chatbot
  }

  get isChatBookingType() {
    return this._stepsStore.connectCalendarStore.isChatBookingType
  }

  get bookingType() {
    return this._stepsStore.connectCalendarStore.bookingType
  }

  get successActions() {
    return this._successActions
  }

  get fallbackActions() {
    return this._fallbackActions
  }

  get successActionsModel() {
    return this._successActionsModel
  }

  get fallbackActionsModel() {
    return this._fallbackActionsModel
  }

  get successActionsErrors() {
    return this._successActionsErrors
  }

  get fallbackActionsErrors() {
    return this._fallbackActionsErrors
  }
  get qualifiedActionsErrors() {
    return this._qualifiedActionsErrors
  }
  get disqualifiedActionsErrors() {
    return this._disqualifiedActionsErrors
  }

  get stepPayload(): IAgentThirdStepPayload {
    return {
      success_actions: this._successActionsModel.toJSON(),
      fallback_actions: this._fallbackActionsModel.toJSON(),
      qualified_actions: this.chatbot.qualifiedActions.toJSON(),
      disqualified_actions: this.chatbot.disqualifiedActions.toJSON(),
      handoff_actions: this.chatbot.handoffActions.toJSON(),
      fields: this._stepsStore.chatbot.fields.list,
      settings: {
        skip_known_fields: { value: this._stepsStore.chatbot.settings.skipKnownFields },
        data_collection_instruction: {
          value: this._stepsStore.chatbot.settings.dataCollectionInstruction,
        },
        qualify_enabled: {
          value: this._stepsStore.chatbot.settings.qualifyEnabled,
        },
        qualify_instruction: {
          value: this._stepsStore.chatbot.settings.qualifyInstruction,
        },
        disqualify_behavior: {
          value: this._stepsStore.chatbot.settings.disqualifyBehavior,
        },
        handoff_enabled: this._stepsStore.chatbot.settings.handoff_enabled,
        handoff_notify_settings: this._stepsStore.chatbot.settings.handoff_notify_settings,
      },
    }
  }

  get dataCollectionErrorAlert(): IAlert | undefined {
    if (this._stepsStore.stepObjectiveStore.stepObjectiveBookingStore.errorAlert) {
      return this._stepsStore.stepObjectiveStore.stepObjectiveBookingStore.errorAlert
    }

    return
  }

  get dataCollectionError() {
    const errorMergeFieldsMinLimit = this._stepsStore.chatbotErrors.getError(
      'settings.data_collection_instruction.merge_fields_min_1'
    )
    const errorMergeFieldsMaxLimit = this._stepsStore.chatbotErrors.getError(
      'settings.data_collection_instruction.merge_fields_max_10'
    )

    if (errorMergeFieldsMinLimit) return errorMergeFieldsMinLimit
    if (errorMergeFieldsMaxLimit) return errorMergeFieldsMaxLimit

    return ''
  }

  get disabled() {
    return this._loading
  }

  onToggle = (section: SectionUnion) => {
    this.isOpened(section)
      ? this._openedSections.delete(section)
      : this._openedSections.add(section)
  }

  isOpened = (section: SectionUnion) => {
    return this._openedSections.has(section)
  }

  private _syncErrors = (errors: IHandleError) => {
    this._stepsStore.chatbotStore.setValidateTrigger()
    this._successActionsErrors.sync(errors)
    this._fallbackActionsErrors.sync(errors)
    this._qualifiedActionsErrors.sync(errors)
    this._disqualifiedActionsErrors.sync(errors)
    this._stepsStore.chatbotErrors.sync(errors)
  }

  private _validate = () => {
    let valid = true
    const isValidate = this._stepsStore.stepObjectiveStore.validateBooking()

    if (isValidate) {
      valid = false
    }

    const isErrorHandoff = this._stepsStore.chatbotStore.stepHandoffStore.validate()
    if (isErrorHandoff) {
      valid = false
    }

    if (!valid) {
      this._stepsStore.chatbotStore.setValidateTrigger()
    }

    return valid
  }

  handleSaveDraft = async (id?: string, shouldRedirect = true) => {
    try {
      if (this._loading) return false

      runInAction(() => {
        this._loading = true
      })

      const valid = this._validate()
      if (!valid) return false

      await this._stepsStore.quickSaveDraft('step_3', { shouldRedirect, id: Number(id) })
      return true
    } catch (e) {
      this._stepsStore.handleApiError(e, this._syncErrors)

      return false
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  handleNextClick = async (id?: string) => {
    try {
      if (this._loading) return false

      runInAction(() => {
        this._loading = true
      })

      const valid = this._validate()
      if (!valid) return false

      await this._stepsStore.handleStepValidationAndNavigation('step_3', this.stepPayload, id)
    } catch (e) {
      this._stepsStore.handleValidationApiError(e, this._syncErrors)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._disposeUpdateOpenedSections?.()
  }

  handleBookingTypeChange = () => {
    const integrationKey = this._stepsStore.connectCalendarStore.selectedIntegrationKey
    const bookAppointmentLinkId = this._stepsStore.connectCalendarStore.eventLink?.data?.id
    if (integrationKey && bookAppointmentLinkId) {
      void this._stepsStore.bookingFieldsStore.fetchFields({
        params: { id: bookAppointmentLinkId, integrationKey },
      })
    }
  }
}
